import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  GridCellParams,
  GridColDef,
  GridRowParams,
  GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import { useIsDirector } from 'shared/permissions/useIsDirector';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Chip } from '@mui/material';
import { dateCell, currencyCell } from 'components/cells';
import {
  filterOperators,
  dateFilterOperators,
  numberFilterOperators,
  singleSelectFilterOperators
} from 'components/enhanced';

const strikedThroughRenderCell = (params: GridCellParams) =>
  params.row.status === 'CANCELED' && 'line-through';

const statusColor = (statusString: string) => {
  // Funzione per determinare il colore del testo in base allo stato, "! = important" forza il comportamento della MUI
  switch (statusString) {
    case 'ACTIVE':
      return '!bg-orange-50 !text-orange-700 !border-orange-200';
    case 'COMPLETED':
      return '!bg-green-50 !text-green-700 !border-green-200';
    default:
      return '!bg-gray-50 !text-gray-600 !border-gray-200';
  }
};

export function PurchaseOrderColumns(
  statusOptions,
  openChangeStatus,
  editOrder,
  downloadAttachment,
  regenerateDocument
): GridColDef[] {
  const { t } = useTranslation();
  const notCloseOrder = useIsDirector() || useIsTechnician();
  const isAdmin = useIsTenantAdministrator();
  const canSeeDetails = isAdmin || notCloseOrder;

  return [
    {
      field: 'ref',
      headerName: 'Ordine',
      filterOperators,
      sortable: true,
      cellClassName: strikedThroughRenderCell,
      flex: 0,
      width: 100
    },
    {
      field: 'created_at',
      headerName: t(`entities.movement.timestamp`),
      filterOperators: dateFilterOperators,
      cellClassName: strikedThroughRenderCell,
      valueFormatter: ({ value }) =>
        value && dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY') : '',
      renderCell: dateCell
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      type: 'singleSelect',
      editable: false,
      sortable: true,
      valueOptions: statusOptions.map((type) => ({
        label: type.display_name,
        value: String(type.value)
      })),
      width: 100,
      filterOperators: singleSelectFilterOperators,
      cellClassName: strikedThroughRenderCell,
      renderCell: (params) => {
        return (
          <Chip
            label={t(`entities.order_head.${params.value}`)}
            className={`${statusColor(params.value)} cursor-pointer`}
            onClick={(e) => {
              e.stopPropagation(); // Evita che il click selezioni l'intera riga
              if (notCloseOrder) return;
              openChangeStatus(params.row);
            }}
          />
        );
      }
    },
    {
      field: 'supplier',
      headerName: t(`entities.order.supplier`),
      flex: 1,
      filterOperators,
      sortable: true,
      cellClassName: strikedThroughRenderCell,
      valueGetter: (params) => params.row.supplier || ''
    },
    {
      field: 'warehouse',
      headerName: t(`entities.order.warehouse`),
      flex: 1,
      filterOperators,
      sortable: true,
      cellClassName: strikedThroughRenderCell,
      valueGetter: (params) => params.row.warehouse || ''
    },
    {
      field: 'total_amount',
      headerName: t(`entities.order.total_amount`),
      width: 150,
      filterOperators: numberFilterOperators,
      cellClassName: (params: GridCellParams) => params.row.status === 3 && 'line-through',
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value, { significantDigits: 3 });
      },
      renderCell: currencyCell
    },
    {
      field: 'actions',
      type: 'actions',
      flex: 0,
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        const actions = [];
        if (canSeeDetails) {
          actions.push({
            label: t(`actions.edit`),
            visible: isAdmin,
            icon:
              params.row.status !== 'ACTIVE' ? (
                <RemoveRedEyeIcon fontSize="small" />
              ) : (
                <EditIcon fontSize="small" />
              ),
            onClick: () => editOrder(params.row.id)
          });
        }
        if (params.row.attachment) {
          actions.push({
            label: t(`actions.download`),
            visible: isAdmin,
            icon: <CloudDownloadIcon />,
            onClick: () => downloadAttachment(params.row)
          });
        }
        actions.push({
          label: t(`actions.regenerate`),
          visible: isAdmin,
          icon: <ReplayIcon />,
          onClick: () => regenerateDocument(params.row)
        });
        return actions;
      }
    } as GridColDef
  ];
}
